import { Badge } from '@cloudflare/kumo';
import { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { palette } from '../../lib/palette';
import { ICONS, Glyph } from '../ui/icons';
import { getBlock, getBlockStyle } from './flowBlocks';

interface BaseNodeProps extends NodeProps {
  /** Sobrescreve o nome padrão do bloco (flowBlocks.ts) */
  label?: string;
  description?: string;
  preview?: React.ReactNode;
  onClick?: () => void;
  onDelete?: () => void;
  /** Substitui o chip de status padrão (Configurado / Configurar) */
  status?: React.ReactNode;
  /** Substitui a saída padrão (handle inferior); use null para bloco final */
  sourceHandles?: React.ReactNode;
  children?: React.ReactNode;
  width?: number;
}

export const BaseNode = memo(({ id, type, data, selected, label: labelOverride, description, preview, onClick, onDelete, status, sourceHandles, children, width = 220 }: BaseNodeProps & { id: string; data: any }) => {
  const blockType = data.nodeType || type;
  const block = getBlock(blockType);
  const style = getBlockStyle(blockType);
  const label = labelOverride || block.label;
  const isConfigured = data.config && Object.keys(data.config).length > 0;
  const [isEditing, setIsEditing] = useState(false);
  const [customLabel, setCustomLabel] = useState(data.customLabel || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { setNodes } = useReactFlow();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleLabelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomLabel(e.target.value);
  };

  const handleLabelBlur = () => {
    setIsEditing(false);
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, customLabel } }
          : node
      )
    );
  };

  const handleLabelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLabelBlur();
    } else if (e.key === 'Escape') {
      setCustomLabel(data.customLabel || '');
      setIsEditing(false);
    }
  };

  const displayLabel = customLabel || label;
  // Com nome personalizado, mostra o tipo do bloco; senão, o grupo (exceto no Início)
  const groupLabel = BLOCK_GROUP_LABEL(block.group);
  const subtitle = customLabel && customLabel !== label ? label : groupLabel !== label ? groupLabel : '';

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        background: 'var(--surface)',
        border: `1px solid ${selected ? palette.blue.base : palette.gray.light2}`,
        boxShadow: selected
          ? `0 0 0 3px ${palette.blue.light2}`
          : '0 4px 10px -4px rgba(0, 30, 43, 0.3)',
        borderRadius: 12,
        cursor: 'pointer',
        width,
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
      }}
      className="group"
    >
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Remover bloco ${displayLabel}`}
          title="Remover bloco"
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'var(--surface)',
            border: `1px solid ${palette.gray.light2}`,
            color: palette.gray.dark1,
            boxShadow: '0 2px 6px rgba(0, 30, 43, 0.15)',
            opacity: 0,
            transition: 'opacity 150ms ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
          }}
          className="group-hover:!opacity-100 focus-visible:!opacity-100"
        >
          <ICONS.Trash size={16} />
        </button>
      )}

      <Handle
        type="target"
        position={Position.Top}
        style={{ width: 10, height: 10, background: palette.gray.base, border: '2px solid var(--surface)' }}
      />

      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            aria-hidden
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: style.tint,
              color: style.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Glyph icon={block.iconName} size={16}/>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={customLabel}
                onChange={handleLabelChange}
                onBlur={handleLabelBlur}
                onKeyDown={handleLabelKeyDown}
                aria-label="Nome do bloco"
                style={{
                  width: '100%',
                  padding: '2px 6px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: palette.gray.dark3,
                  border: `1px solid ${palette.blue.base}`,
                  borderRadius: 4,
                  outline: 'none',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div onClick={handleLabelClick} style={{ cursor: 'text' }} title="Clique para renomear">
                <div style={{ fontSize: 13, fontWeight: 600, color: palette.gray.dark3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayLabel}
                </div>
                {subtitle && (
                  <div style={{ fontSize: 11, color: palette.gray.dark1, marginTop: 1 }}>{subtitle}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {description && (
          <p style={{ fontSize: 12, color: palette.gray.dark1, margin: '10px 0 0', lineHeight: 1.4 }}>{description}</p>
        )}

        {preview && <div style={{ marginTop: 8 }}>{preview}</div>}

        {children}

        <div style={{ marginTop: 10 }}>
          {status !== undefined
            ? status
            : isConfigured ? <Badge variant="green">Configurado</Badge> : <Badge variant="orange">Configurar</Badge>}
        </div>
      </div>

      {sourceHandles !== undefined ? sourceHandles : (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ width: 10, height: 10, background: palette.gray.dark1, border: '2px solid var(--surface)' }}
        />
      )}
    </div>
  );
});

const BLOCK_GROUP_LABEL = (group: string) =>
  ({ inicio: 'Início', mensagens: 'Mensagem', logica: 'Lógica', integracoes: 'Integração' } as Record<string, string>)[group] || '';

BaseNode.displayName = 'BaseNode';
